import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";

// components imports
import ModernView from "./Components/ModernView";
import PrintableInvoice from "../InvoiceModal/Components/PrintableInvoice";
import CarrierTableLoader from "../CarrierInnerTableLoader/CarrierInnerTableLoader";
import Logo from "../../assets/images/hajex_logo.png";
import LogoOnDark from "../logo-dark.png";

// apis imports
import { fetchAdminShipmentDataById } from "../../services/ShipperServices/Shipments/shipments.services";
import axiosInstance from "../../services/axiosInstance";

// library imports
import Backdrop from "@mui/material/Backdrop";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import html2pdf from "html2pdf.js";
import { MdArrowCircleDown, MdContentCopy, MdClose } from "react-icons/md";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

/**
 * How many invoice WRITES are in flight right now, in either view.
 *
 * The view switch must not fire mid-save: the view being left unmounts with
 * the save's result snackbar, and the view being entered fetches a copy of the
 * shipment that can predate the write — a Modify seeded from it posts stale
 * absolutes. Neither view's write code can be edited to report this (Classic is
 * production; the Modern panels are owned elsewhere), and every one of them
 * goes through the shared axios instance, so the count is taken there.
 *
 * The interceptors are PASS-THROUGH: they return the exact config and response
 * objects they were handed and never touch a header, URL or body, so what goes
 * on the wire is unchanged. Only non-GET requests are counted — the page's own
 * grid refreshes and the invoice loads are reads. Installed only while `active`
 * (an invoice is open), ejected and reset to 0 when it closes, so a request
 * that never settles can at worst lock the switch until the invoice is closed.
 */
export function useInvoiceWritesInFlight(active) {
  const [inFlight, setInFlight] = useState(0);

  useEffect(() => {
    if (!active) return undefined;
    let live = true;
    const pending = new WeakSet();

    const settle = (config) => {
      if (!config || !pending.has(config)) return;
      pending.delete(config);
      if (live) setInFlight((n) => Math.max(0, n - 1));
    };

    const requestId = axiosInstance.interceptors.request.use((config) => {
      const method = String(config?.method || "get").toLowerCase();
      if (live && method !== "get" && method !== "head" && method !== "options") {
        pending.add(config);
        setInFlight((n) => n + 1);
      }
      return config;
    });
    const responseId = axiosInstance.interceptors.response.use(
      (response) => {
        settle(response?.config);
        return response;
      },
      (error) => {
        settle(error?.config);
        return Promise.reject(error);
      },
    );

    return () => {
      live = false;
      axiosInstance.interceptors.request.eject(requestId);
      axiosInstance.interceptors.response.eject(responseId);
      setInFlight(0);
    };
  }, [active]);

  return inFlight;
}

/**
 * Admin Invoice Workbench — the Modern-design invoice.
 *
 * This is a PARALLEL screen. InvoiceModal and every component it owns are
 * untouched; this one imports them. Classic view mounts the real
 * <EditableInvoice/>, so switching to it gives byte-identical production
 * behaviour.
 *
 * setShipments / getAllShipments / setSnack are OPTIONAL: three Dispatcher
 * call sites mount the invoice without them, and production throws there after
 * a successful write because it calls setShipments unconditionally.
 */
export default function AdminInvoiceWorkbench({
  open,
  setOpen,
  data,
  setShipments,
  getAllShipments,
  setSnack,
  /* Switching views swaps which modal is mounted, rather than nesting one
     inside the other. EditableInvoice's wrapper is `position:absolute` centred
     at 50/50 with 90% width — built to BE the modal — so rendering it inside
     this shell floated it on top of the Modern view. The page above owns the
     view state and mounts one or the other. */
  onSwitchToClassic,
  /* True while any invoice write is in flight (useInvoiceWritesInFlight on
     the page). The "Classic view" button is locked for the duration. */
  switchBlocked = false,
}) {
  const { t } = useTranslation("admin");
  const invoiceRef = useRef(null);

  const [shipmentData, setShipmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // The failed load was the refresh a production component asks for AFTER a
  // successful write (one-time-shipper confirm, package images). The error
  // card must not then claim that nothing was written.
  const [loadErrorAfterWrite, setLoadErrorAfterWrite] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [copied, setCopied] = useState("");

  /* Every load gets a sequence number and only the newest may land, so an
     out-of-order GET (the 500ms post-confirm refresh racing a reopen, a slow
     "Try again") cannot overwrite a newer copy of the shipment. */
  const fetchSeq = useRef(0);
  /* One session per time this modal is shown. setData handed to the panels
     is bound to the session it was rendered in, so a write that resolves
     after the modal was closed or switched away from is dropped instead of
     landing on the next shipment — or on `null`, where a functional updater
     such as the customs delete filter threw and took the page down. */
  const liveSession = useRef(0);
  const [session, setSession] = useState(0);

  const snack = useCallback(
    (text, severity) =>
      setSnack?.((prev) => ({ ...prev, open: true, text, severity })),
    [setSnack],
  );

  const handleClose = () => {
    setOpen(false);
    // Production never resets this, so a failed fetch shows the previous
    // shipment's numbers under the next row click. Clearing here matters more
    // in a screen that can be reopened repeatedly.
    setShipmentData(null);
    setLoadError(null);
    setDownloadOpen(false);
  };

  /* Any way this modal stops being shown — its own Close or Escape, "Classic
     view", or the Classic invoice's Close after a switch (which never runs
     handleClose above) — ends the session: late loads and late writes from
     it are dropped, and the next open starts clean and refetches. */
  useEffect(() => {
    if (open) return;
    fetchSeq.current += 1;
    liveSession.current += 1;
    setSession(liveSession.current);
    setShipmentData(null);
    setLoadError(null);
    setLoadErrorAfterWrite(false);
    setDownloadOpen(false);
  }, [open]);

  const setDataForSession = useMemo(() => {
    const token = session;
    return (next) => {
      if (liveSession.current !== token) return;
      setShipmentData((prev) => {
        if (prev == null) return prev;
        return typeof next === "function" ? next(prev) : next;
      });
    };
  }, [session]);

  /* ── data ────────────────────────────────────────────────────────────
     The bucket split is duplicated from InvoiceModal.jsx:148-180 rather than
     extracted, so that file keeps working exactly as it does today. The
     string comparisons are STRICT on purpose: `==` or Number() silently
     reclassifies money between panels. */
  const getShipmentDataById = useCallback(
    async (id, afterWrite = false) => {
      const seq = ++fetchSeq.current;
      setLoading(true);
      setLoadError(null);

      const response = await fetchAdminShipmentDataById(id);

      if (seq !== fetchSeq.current) return;

      if (response.error) {
        setLoading(false);
        setShipmentData(null);
        setLoadError(response.error);
        if (afterWrite) setLoadErrorAfterWrite(true);
        snack(i18n.t("common:messages.somethingWentWrong"), "error");
        return;
      }

      const rawResult = response?.data?.result;
      const { validCharges, removedCharges, quoteCharges, estCharges } = (
        rawResult?.shipment_charges || []
      ).reduce(
        (acc, charge) => {
          if (!charge?.invoice_id) {
            if (charge?.is_for_user === "0") {
              acc.quoteCharges.push(charge);
            } else if (charge?.is_for_user === "2") {
              // production gates this on type === "editable"; this screen is
              // always the admin/editable one.
              acc.estCharges.push(charge);
            } else {
              acc.validCharges.push(charge);
            }
          } else {
            acc.removedCharges.push(charge);
          }
          return acc;
        },
        {
          validCharges: [],
          removedCharges: [],
          quoteCharges: [],
          estCharges: [],
        },
      );

      setShipmentData({
        ...rawResult,
        shipment_charges: validCharges,
        billingAutomation: removedCharges,
        quoteCharges,
        estCharges,
      });
      setLoadErrorAfterWrite(false);
      setLoading(false);
    },
    [snack],
  );

  /* Handed to production components (OneTimeShipperAdminApproval), which call
     it 500ms after a successful write. Bound to the session like setData: if
     the modal was closed or switched away from in those 500ms, the same GET
     still goes out (Classic sends it too) but its result is discarded rather
     than overwriting whatever this modal shows next. */
  const refreshShipmentData = useMemo(() => {
    const token = session;
    return (shipmentId) => {
      if (!shipmentId) return;
      if (liveSession.current !== token) {
        fetchAdminShipmentDataById(shipmentId);
        return;
      }
      getShipmentDataById(shipmentId, true);
    };
  }, [session, getShipmentDataById]);

  /* Gated on `open`, not just on `data`. Both invoices now share one id from
     the page above, so fetching on `data` alone fired a second
     /admin/shipment/get-by-id call every time an admin opened the Classic
     view — invisible, but a duplicated request on every invoice open. */
  useEffect(() => {
    if (open && data) getShipmentDataById(data);
  }, [open, data, getShipmentDataById]);

  /* ── downloads ───────────────────────────────────────────────────────
     downloadPdf runs over the HIDDEN <PrintableInvoice/>, which renders
     BillingUser — the customer-facing breakdown. It must never be pointed at
     the Modern layout or internal cost and markup land on a customer PDF. */
  const downloadPdf = async (shipment) => {
    const input = invoiceRef.current;
    const options = {
      filename: `${
        shipment.tattension + " " + shipment.shipment_tracking_id
      } Invoice.pdf`,
      format: "A4",
      jsPDF: { unit: "px", format: [830, 1000], orientation: "p" },
      image: { type: "jpeg", quality: 0.98 },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      html2canvas: { scale: 2, useCORS: true },
    };
    await html2pdf().set(options).from(input).save();
  };

  const downloadDocs = async (shipment, docType) => {
    const DOWNLOAD_CONFIG = {
      label: { fileKey: "single_shipping_labels", label: t("invoice.label") },
      declaration: { fileKey: "single_shipping_invoice", label: t("invoice.declaration") },
    };

    const config = DOWNLOAD_CONFIG[docType];
    if (!config) {
      snack(t("invoice.invalidDownloadType"), "error");
      return;
    }

    const { fileKey, label } = config;
    try {
      const fileUrl = shipment?.[fileKey]?.file;
      if (!fileUrl) throw new Error("File URL missing");

      const response = await fetch(fileUrl, {
        method: "GET",
        headers: { "Content-Type": "application/pdf" },
      });
      if (!response.ok) throw new Error(`Failed with status ${response.status}`);

      const blob = await response.blob();
      if (!blob || blob.size === 0) throw new Error("Empty file");

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${shipment?.tattension} ${shipment?.shipment_tracking_id} ${label}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`[${label} Download Error]`, error);
      snack(t("common:messages.somethingWentWrong"), "error");
    }
  };

  const copy = (text, key) => {
    if (!text) return;
    navigator.clipboard?.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 2000);
  };

  const d = shipmentData;

  /* Revision marker, read exactly as Classic's Header.jsx:181-198 reads it:
     `*N` when there is more than one valid revision, and the second-to-last
     revision's `total` beside it, raw (no rounding, no `$`), struck through.
     `shipment_charges` here is the same post-split list Classic's Header
     gets, and the ledger's applyResult appends the response row after an Add
     or a Modify exactly as BillingAdmin does — so after either write this
     shows the same N and previous total Classic shows in that state, until a
     reopen refetches. Display only: nothing is written back. */
  const revisionCount = d?.shipment_charges?.length ?? 0;
  const previousTotal =
    revisionCount > 1 ? d.shipment_charges[revisionCount - 2]?.total : null;

  const lane = [d?.fcountry, d?.tcountry].filter(Boolean);
  const laneLabel = lane.length
    ? lane[0] === lane[1]
      ? t("invoice.laneDomestic", { country: lane[0] })
      : t("invoice.laneCrossBorder", { from: lane[0], to: lane[1] })
    : t("invoice.laneUnknown");

  return (
    <Modal
      aria-labelledby="admin-invoice-workbench"
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={open}>
        <div className="fixed inset-0 flex items-center justify-center p-2 sm:p-3 outline-none">
          <div className="relative flex h-full max-h-[96vh] w-full max-w-[1460px] flex-col overflow-hidden rounded-[11px] bg-ui-page shadow-ui-md">
            {/* ── Header ─────────────────────────────────────────────── */}
            <div className="flex flex-none flex-wrap items-center gap-2.5 border-b border-ui-border bg-ui-surface px-2.5 py-1.5">
              {/* hajex_logo.png is black "HAJEX" on transparent and vanishes
                  on the dark surface, so dark mode swaps in the Navbar's
                  light-lettered logo. A `dark:` class rather than
                  useThemeMode(), which throws outside a ThemeModeProvider. */}
              <img
                src={Logo}
                alt="Hajex Bolt"
                className="block h-[19px] w-auto flex-none dark:hidden"
              />
              <img
                src={LogoOnDark}
                alt="Hajex Bolt"
                className="hidden h-[19px] w-auto flex-none dark:block"
              />
              <span className="flex-none rounded border border-state-purple-border bg-state-purple-bg px-1.5 py-px font-manrope text-[8.5px] font-bold uppercase tracking-[0.06em] text-state-purple-text">
                {t("invoice.beta")}
              </span>
              <div className="h-5 w-px flex-none bg-ui-border" />

              <button
                onClick={() => copy(d?.invoice_id, "inv")}
                title={t("invoice.copyInvoiceNumber")}
                className="flex-none font-jakarta text-[14px] font-extrabold text-ui-text hover:text-brand"
              >
                {d?.invoice_id ?? "—"}
                {copied === "inv" && (
                  <span className="ml-1.5 font-manrope text-[10px] font-semibold text-state-success-text">
                    {t("invoice.copied")}
                  </span>
                )}
              </button>

              <span className="min-w-0 flex-1 truncate font-manrope text-[12px] font-medium text-ui-text-muted">
                {d?.created_by?.username?.trim() ||
                  d?.user?.username?.trim() ||
                  "—"}
                {" · "}
                {d?.created_by?.id || d?.user?.id || "—"}
              </span>

              <div className="flex flex-none items-baseline gap-1.5">
                <span className="font-jakarta text-[10px] font-semibold uppercase tracking-[0.07em] text-ui-text-muted">
                  {t("invoice.total")}
                </span>
                <span className="font-manrope text-[15px] font-extrabold tabular-nums text-ui-text">
                  {d?.Total != null ? `${d.Total}$` : "—"}
                  {/* inside the amount, not a sibling: a flex item ignores
                      the <sup>'s vertical-align and would sit on the line */}
                  {revisionCount > 1 && (
                    <sup
                      title={t("invoice.chargeRevisions", { count: revisionCount })}
                      className="ml-px text-[10px] font-bold text-ui-text-muted"
                    >
                      {`*${revisionCount}`}
                    </sup>
                  )}
                </span>
                {previousTotal != null && previousTotal !== "" && (
                  <span
                    title={t("invoice.totalBeforeTheLatestRevision")}
                    className="font-manrope text-[12px] font-medium tabular-nums text-ui-text-muted line-through"
                  >
                    {previousTotal}
                  </span>
                )}
              </div>

              <div className="relative flex-none">
                <button
                  onClick={() => setDownloadOpen((v) => !v)}
                  disabled={!d}
                  className="flex items-center gap-1.5 rounded-[5px] border border-brand bg-brand px-2.5 py-1.5 font-manrope text-[11px] font-semibold text-brand-contrast hover:bg-brand-hover disabled:opacity-50"
                >
                  <MdArrowCircleDown className="h-4 w-4" />
                  {t("common:actions.download")}
                </button>
                {downloadOpen && d && (
                  <div className="absolute right-0 top-[34px] z-[60] min-w-[190px] rounded-[7px] border border-ui-border bg-ui-surface p-1 shadow-ui-md">
                    {[
                      {
                        label: t("invoice.invoicePdf"),
                        run: () => downloadPdf(d),
                        show: true,
                      },
                      {
                        label: t("invoice.shippingLabel"),
                        run: () => downloadDocs(d, "label"),
                        show: true,
                      },
                      {
                        label: t("invoice.customsDeclaration"),
                        run: () => downloadDocs(d, "declaration"),
                        show: !!d?.single_shipping_invoice,
                      },
                    ]
                      .filter((i) => i.show)
                      .map((item) => (
                        <button
                          key={item.label}
                          onClick={() => {
                            setDownloadOpen(false);
                            item.run();
                          }}
                          className="block w-full rounded px-2 py-1.5 text-left font-manrope text-[11px] font-semibold text-ui-text hover:bg-ui-surface-sunken"
                        >
                          {item.label}
                        </button>
                      ))}
                  </div>
                )}
              </div>

              <button
                onClick={handleClose}
                title={t("common:actions.close")}
                className="flex h-[27px] w-7 flex-none items-center justify-center rounded-[5px] border border-ui-border bg-ui-surface text-ui-text-muted hover:border-state-danger-border hover:bg-state-danger-bg hover:text-state-danger-text"
              >
                <MdClose className="h-4 w-4" />
              </button>
            </div>

            {/* ── Meta strip ─────────────────────────────────────────── */}
            {d && (
              <div className="flex flex-none flex-wrap items-center gap-1.5 border-b border-ui-border bg-ui-surface-sunken px-2.5 py-1.5">
                {[
                  [
                    d?.shipping_company?.shipping_company?.name,
                    d?.service_type,
                  ]
                    .filter(Boolean)
                    .join(" · "),
                  t("invoice.chipPackages", {
                    packages: d?.shipment_details?.length ?? 0,
                    weight: d?.total_weight ?? 0,
                    unit: d?.unit === "Imperial" ? "lb" : "kg",
                  }),
                  d?.shipment_type,
                  t("invoice.chipAccount", {
                    name: d?.shipping_company?.account_name ?? t("invoice.defaultAccount"),
                  }),
                ]
                  .filter(Boolean)
                  .map((chip) => (
                    <span
                      key={chip}
                      className="rounded border border-ui-border bg-ui-surface px-[7px] py-0.5 font-manrope text-[10.5px] font-semibold text-ui-text"
                    >
                      {chip}
                    </span>
                  ))}
                <span className="rounded border border-state-info-border bg-state-info-bg px-[7px] py-0.5 font-manrope text-[10.5px] font-semibold text-state-info-text">
                  {laneLabel}
                </span>

                <div className="flex-1" />

                <div className="flex flex-none items-center gap-1">
                  <span className="font-manrope text-[11.5px] font-medium text-ui-text-muted">
                    {d?.shipment_tracking_id ?? "—"}
                  </span>
                  <button
                    onClick={() => copy(d?.shipment_tracking_id, "trk")}
                    title={t("invoice.copyTrackingNumber")}
                    className="rounded border border-ui-border bg-ui-surface px-1.5 py-0.5 text-ui-text-muted hover:bg-ui-surface-sunken"
                  >
                    {copied === "trk" ? (
                      <span className="font-manrope text-[10px] font-semibold text-state-success-text">
                        {t("invoice.copied")}
                      </span>
                    ) : (
                      <MdContentCopy className="h-3 w-3" />
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* ── Body ───────────────────────────────────────────────── */}
            <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden bg-ui-page p-2">
              {loading && (
                <div className="p-4">
                  <CarrierTableLoader />
                </div>
              )}

              {!loading && loadError && (
                <div className="flex h-full items-center justify-center p-4">
                  <div className="flex max-w-[480px] flex-col items-center gap-2.5 rounded-[9px] border border-state-danger-border bg-ui-surface p-6 text-center">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full border border-state-danger-border bg-state-danger-bg font-manrope text-base font-extrabold text-state-danger-text">
                      !
                    </div>
                    <h3 className="m-0 font-jakarta text-sm font-extrabold text-ui-text">
                      {t("invoice.thisInvoiceDidntLoad")}
                    </h3>
                    {loadErrorAfterWrite ? (
                      <p className="m-0 font-manrope text-[11.5px] font-medium leading-relaxed text-ui-text-secondary">
                        {t("invoice.loadErrorAfterWrite")}
                      </p>
                    ) : (
                      <p className="m-0 font-manrope text-[11.5px] font-medium leading-relaxed text-ui-text-secondary">
                        {t("invoice.loadError")}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => getShipmentDataById(data)}
                        className="rounded-[5px] border border-brand bg-brand px-3 py-1.5 font-manrope text-[11.5px] font-semibold text-brand-contrast hover:bg-brand-hover"
                      >
                        {t("common:actions.tryAgain")}
                      </button>
                      <button
                        onClick={handleClose}
                        className="rounded-[5px] border border-ui-border bg-ui-surface px-3 py-1.5 font-manrope text-[11.5px] font-semibold text-ui-text hover:bg-ui-surface-sunken"
                      >
                        {t("common:actions.close")}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {!loading && !loadError && d && (
                <div className="flex flex-col gap-2">
                  {/* view switch */}
                  <div className="flex flex-wrap items-center gap-2 rounded-[7px] border border-ui-border bg-ui-surface px-2 py-1.5">
                    <span className="font-jakarta text-[10px] font-semibold uppercase tracking-[0.07em] text-ui-text-muted">
                      {t("invoice.viewLabel")}
                    </span>
                    <div className="flex gap-[3px] rounded-md bg-ui-surface-sunken p-0.5">
                      <button
                        type="button"
                        className="rounded bg-ui-surface px-3 py-[3px] font-manrope text-[11.5px] font-semibold text-ui-text shadow-ui-sm"
                      >
                        {t("invoice.modernView")}
                      </button>
                      {/* Locked while a save is in flight: the Classic
                          invoice remounts and refetches on the way in, and
                          must not fetch before the write has landed. */}
                      <button
                        type="button"
                        onClick={onSwitchToClassic}
                        disabled={switchBlocked}
                        title={
                          switchBlocked
                            ? t("invoice.saveInProgress")
                            : undefined
                        }
                        className="rounded bg-transparent px-3 py-[3px] font-manrope text-[11.5px] font-semibold text-ui-text-muted hover:text-ui-text disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-ui-text-muted"
                      >
                        {t("invoice.classicView")}
                      </button>
                    </div>
                    <span className="font-manrope text-[11.5px] font-medium text-ui-text-muted">
                      {switchBlocked
                        ? t("invoice.savingSwitchLocked")
                        : t("invoice.modernViewNote")}
                    </span>
                  </div>

                                      <ModernView
                      data={d}
                      setData={setDataForSession}
                      setShipments={setShipments}
                      getAllShipments={getAllShipments}
                      refreshShipmentData={refreshShipmentData}
                      setSnack={setSnack}
                      downloadDocs={downloadDocs}
                    />
                </div>
              )}
            </div>

            {/* ── Footer ─────────────────────────────────────────────
                No "Save changes": every control here saves on interaction,
                exactly as production does. A batched payload has never
                reached manage-invoice. */}
            <div className="flex flex-none items-center gap-2.5 border-t border-ui-border bg-ui-surface px-2.5 py-1.5">
              <span className="font-manrope text-[11.5px] font-medium text-ui-text-muted">
                {t("invoice.savesAsYouGo")}
              </span>
              <div className="flex-1" />
              <button
                onClick={handleClose}
                className="rounded-[5px] border border-ui-border bg-ui-surface px-3 py-1.5 font-manrope text-[11px] font-semibold text-ui-text hover:bg-ui-surface-sunken"
              >
                {t("common:actions.close")}
              </button>
            </div>

            {/* Hidden print surface — renders BillingUser, never the admin
                layout. Same ref contract as production. */}
            <div
              style={{
                zIndex: "-1000",
                position: "absolute",
                visibility: "hidden",
                left: "-1000",
              }}
            >
              <PrintableInvoice invoiceRef={invoiceRef} data={shipmentData} />
            </div>
          </div>
        </div>
      </Fade>
    </Modal>
  );
}
